/**
 * SecretsCatalog — **Connections** UI for the reusable tool-secret registry (workspace + network).
 * Lists ``GET /tools/secrets-registry`` rows. **Create New** first asks which MCP tool needs
 * a stored-connection (**service** MCP tools whose catalog schema includes ``x-secret`` fields —
 * ``generic-rest-api``, ``code-connector``, and future equivalents), then opens

 * ``ToolGenericRestConfigWizard`` or ``ToolCodeConnectorRegistryWizard`` accordingly.
 *
 * When ``catalogNetworkId`` is set (network workbench), tenant-wide rows remain visible
 * alongside connections scoped to that network.
 *
 * PR5: listens for ``stepflow-secret-registry-changed`` (user + workbench channel) to
 * silently refresh; “Where used” calls ``GET /tools/secrets-registry/{id}/references``;
 * deletes call ``DELETE /tools/secrets-registry/{id}`` (blocked server-side while agents mount the id).
 *
 * **Probes:** “Test connection” / row **Test** are not gated on having a workspace network.
 * Registry secrets are tenant-scoped; ``POST /tools/probe`` and ``…/test`` authorize via JWT
 * (and ``__registry_probe__`` when no network id is in UI context). Do not add a UI flag to
 * disable probes here — that only hid a valid workflow.
 * The list API includes ``probeMethod`` / ``probePath`` (from saved ``PROBE_*`` or ``API_SPECIFICATION``)
 * because list rows omit ``config`` — row **Test** uses those so the MCP request targets your REST path,
 * not the tenant root (which often 302s to a browser shell such as ``/jira/for-you``).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Cable, Copy, Pencil, Plus, RefreshCw, Search, Trash2, Eye } from 'lucide-react';
import WorkspaceViewDialog from '../workspace/WorkspaceViewDialog.jsx';
import {
  createMcpCredential,
  deleteWorkspace,
  deleteMcpCredential,
  deleteSecretsRegistrySecret,
  getSecretsRegistryReferences,
  getSecretsRegistrySecret,
  listMcpCredentials,
  listMcpRegistry,
  listSecretsRegistry,
  listTools,
  revealMcpCredentialScoped,
  secretsRegistryV1Enabled,
  testMcpCredentialScoped,
  testSecretsRegistrySecret,
} from '../../services/toolsApi';
import { probeRequestFromGenericRestRegistryRow } from '../../services/genericRestProbe';
import ToolGenericRestConfigWizard from '../agentTools/ToolGenericRestConfigWizard.jsx';
import ToolCodeConnectorRegistryWizard, {
  mapRegistryCodeConnectorSecretToInitial,
} from '../agentTools/ToolCodeConnectorRegistryWizard.jsx';
import ToolWorkspaceRegistryWizard from '../agentTools/ToolWorkspaceRegistryWizard.jsx';
import McpCredentialWizardDialog from '../mcp/McpCredentialWizardDialog.jsx';
import ToolSelectDialog from '../agentTools/ToolSelectDialog.jsx';
import PortalMenu from '../PortalMenu.jsx';
import DeleteConnectionDialog from './DeleteConnectionDialog.jsx';
import {
  canEditDeleteRegistrySecret,
  registrySecretInheritedHere,
} from '../../utils/secretsRegistryRowActions.js';

const TOOLS_ENV =
  (typeof import.meta !== 'undefined' &&
    (import.meta.env?.VITE_TOOLS_ENV || import.meta.env?.VITE_ENV)) ||
  'dev';

/** ``POST /tools/probe`` — no workspace network row; backend accepts tenant-only auth (see tools_impl). */
const REGISTRY_PROBE_NETWORK_ID = '__registry_probe__';

/** Hydrate wizard ``initialEntry`` from ``GET …/secrets-registry/{id}`` (full Dynamo row JSON). */
export function mapRegistrySecretToWizardInitial(secret) {
  if (!secret || typeof secret !== 'object') return undefined;
  const cfg = { ...(secret.config || {}) };
  const at = String(secret.authType || '').trim();
  if (at) cfg.AUTH_TYPE = cfg.AUTH_TYPE || at;
  if (!String(cfg.AUTH_TYPE || '').trim()) cfg.AUTH_TYPE = 'Bearer';
  let v = secret.version;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    const n = v != null ? Number(v) : NaN;
    v = Number.isFinite(n) ? n : undefined;
  }
  return {
    instanceId: 'ti_registry_existing',
    instanceLabel: String(secret.secretName || '').trim(),
    secretId: String(secret.secretId || '').trim(),
    secretName: secret.secretName,
    config: cfg,
    secretRefs: secret.secretRefs && typeof secret.secretRefs === 'object' ? { ...secret.secretRefs } : {},
    registryLogicalVersion: typeof v === 'number' ? v : undefined,
  };
}

/**
 * Mirrors ``tools_impl._normalise_input_schema`` so we can derive ``x-secret`` keys even when the
 * API omitted ``secretFields`` (hints are optional on some payloads).
 */
function normalizeCatalogInputSchema(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const inner = 'json' in raw ? raw.json : raw;
  return typeof inner === 'object' && inner !== null ? inner : {};
}

function inferSecretFieldKeysFromCatalogSchema(rawSchema) {
  const schema = normalizeCatalogInputSchema(rawSchema);
  const props = schema.properties;
  if (!props || typeof props !== 'object') return [];
  const out = [];
  for (const [key, spec] of Object.entries(props)) {
    if (spec && typeof spec === 'object' && spec['x-secret'] === true) {
      out.push(key);
    }
  }
  return out;
}

/** Built-in MCP tools whose Connections UX is wired in this package (credential registry). */
const REGISTRY_WIRED_TOOL_NAMES = new Set(['generic-rest-api', 'code-connector', 'workspace']);

/** Curated "Most used" highlights for the Create-New picker. Surfaced as a
 *  badge + sort boost; purely cosmetic, safe to tune as usage data lands. */
const MOST_USED_MCPS = new Set(['github', 'slack', 'postgres', 'fetch']);
const MOST_USED_CONNECTORS = new Set(['generic-rest-api', 'code-connector', 'workspace']);

/** Synthetic catalog tile for ``workspace`` (doc/arch/ASSETS.md). Unlike
 *  connectors there is no backing Lambda — shared workspaces are metadata + S3
 *  objects — so the tile is injected client-side rather than coming from
 *  ``listTools``. Shape matches enough of a catalog row for ToolSelectDialog +
 *  the picker dispatch. (Distinct from the read-only Data Plane Asset.) */
const WORKSPACE_PSEUDO_TOOL = {
  name: 'workspace',
  toolName: 'workspace',
  toolType: 'service',
  displayName: 'Shared workspace',
  description: 'Shared, mutable workspace — upload a zip/tar of starter files; '
    + 'attach it to builder agents that mutate it in place. Reusable across runs and rooms.',
};

/** Tools eligible for Connections — ``service`` + credentials (API hints or schema walk, or wired tools). */
export function toolSupportsRegistryConnections(tool) {
  if (!tool || typeof tool !== 'object') return false;
  const tt = String(tool.toolType || '').trim().toLowerCase();
  if (tt !== 'service') return false;
  const name = String(tool.name || '').trim();
  const fromApi = Array.isArray(tool.secretFields)
    ? tool.secretFields.filter((k) => String(k || '').trim())
    : [];
  const inferred = inferSecretFieldKeysFromCatalogSchema(tool.inputSchema);
  const hinted = Boolean(fromApi.length || inferred.length);

  /** Primary: catalog lists ``secretFields`` or we infer ``x-secret`` from schema. */
  if (hinted) return true;
  /** Fall back: IaC-stable tools whose registry wizards ship here (hints may be missing on truncated rows). */
  return REGISTRY_WIRED_TOOL_NAMES.has(name);
}

/** Row visible when attaching or filtering within a network context */
export function registrySecretVisibleForNetwork(row, catalogNetworkId) {
  if (!catalogNetworkId || catalogNetworkId === 'new') return true;
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'TENANT') return true;
  if (k === 'NETWORK') {
    const nid = String(s.networkId ?? s.network_id ?? '').trim();
    return nid === String(catalogNetworkId).trim();
  }
  return false;
}

function formatScope(row) {
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'NETWORK') return `Network · ${String(s.networkId ?? s.network_id ?? '—')}`;
  if (k === 'POOL') {
    // POOL scope IS the room-wide tier (every agent in the room). Show it as
    // "Room Wide" — the same wording the room settings drawer uses — rather
    // than the internal "Pool · net / pool" coordinates.
    return 'Room Wide (all agents)';
  }
  return 'Tenant-wide';
}

function formatAuthType(row) {
  const a = row.authType;
  if (a == null || String(a).trim() === '') return '—';
  return String(a);
}

/** Explains why Edit/Delete are disabled for inherited / higher-layer registry rows. */
function registryRowMutateHint(row, { allowTenantRegistryMutate }) {
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'TENANT' && !allowTenantRegistryMutate) {
    return 'Tenant-wide connections are managed under Workspace → Settings → Connections (owners and admins only).';
  }
  if (k === 'TENANT') {
    return 'Tenant-wide connections can only be changed by a workspace owner or admin.';
  }
  if (k === 'NETWORK') {
    return 'Network-scoped connections can only be changed by the network designer.';
  }
  if (k === 'POOL') {
    return 'Pool-scoped connections can only be changed by this pool’s creator.';
  }
  return 'You do not have permission to change this connection.';
}

/** Is an MCP-credential row INHERITED at the current mount (i.e. its scope is
 *  broader than where the catalog is mounted)? Inherited rows are read-only;
 *  the user can "Override here" to create a more-specific row. Mount scope is
 *  derived from catalogNetworkId/catalogPoolId (TENANT < NETWORK < POOL). */
function mcpRowIsInherited(row, { catalogNetworkId, catalogPoolId }) {
  const k = String(row?.scope?.kind || 'TENANT').toUpperCase();
  const haveNet = Boolean(catalogNetworkId && catalogNetworkId !== 'new');
  const havePool = Boolean(catalogPoolId);
  // POOL mount: TENANT + NETWORK rows are inherited; POOL row is own.
  if (haveNet && havePool) return k !== 'POOL';
  // NETWORK mount: TENANT row is inherited; NETWORK row is own.
  if (haveNet) return k !== 'NETWORK';
  // TENANT mount: nothing is inherited.
  return false;
}

function RegistryTestStatusBadge({ row }) {
  const raw = String(row.testStatus || 'UNTESTED').toUpperCase();
  let label = 'Not tested';
  let cls = 'border-amber-200 bg-amber-50 text-amber-900';
  if (raw === 'TESTED_OK') {
    label = 'Test OK';
    cls = 'border-emerald-200 bg-emerald-50 text-emerald-900';
  } else if (raw === 'TESTED_FAIL') {
    label = 'Test error';
    cls = 'border-red-200 bg-red-50 text-red-900';
  }
  const tip = row.lastTestError ? String(row.lastTestError) : undefined;
  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
      title={tip}
    >
      {label}
    </span>
  );
}

/** Extra lines for the registry test dialog — generic-rest MCP probe shape only for now. */
function registryProbeSummaryLines(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const probe = payload.result && typeof payload.result === 'object' ? payload.result : null;
  if (!probe) return [];
  const lines = [];
  if (typeof probe.status === 'number') lines.push(`Upstream HTTP status: ${probe.status}`);
  if (probe.method != null || probe.url != null) {
    const m = probe.method != null ? String(probe.method) : '';
    const u = probe.url != null ? String(probe.url) : '';
    lines.push(`Request: ${[m, u].filter(Boolean).join(' ')}`.trim());
  }
  if (typeof probe.durationMs === 'number') lines.push(`Duration: ${probe.durationMs} ms`);
  const h = probe.headers && typeof probe.headers === 'object' ? probe.headers : {};
  const keys = ['Location', 'WWW-Authenticate', 'X-Seraph-Loginreason', 'X-Request-Id', 'X-Arequestid'];
  for (const k of keys) {
    const v = h[k] ?? h[k.toLowerCase()];
    if (v != null && String(v).trim() !== '') lines.push(`${k}: ${String(v)}`);
  }
  return lines;
}

function RegistryProbeResultDialog({ open, title, payload, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const lines = registryProbeSummaryLines(payload);
  let pretty = '';
  try {
    pretty = JSON.stringify(payload ?? {}, null, 2);
  } catch {
    pretty = String(payload);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="registry-probe-dialog-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <h3 id="registry-probe-dialog-title" className="text-sm font-semibold text-gray-900">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
        <div className="max-h-[calc(85vh-8rem)] overflow-y-auto px-4 py-3 text-xs text-gray-800">
          <div className="mb-3 space-y-1 rounded border border-gray-100 bg-gray-50 p-2">
            <div>
              <span className="font-semibold text-gray-700">Stored result: </span>
              <span className="font-mono text-[11px]">{String(payload?.testStatus || '—')}</span>
              {payload?.lastTestedAt ? (
                <span className="ml-2 text-[11px] text-gray-500">{String(payload.lastTestedAt)}</span>
              ) : null}
            </div>
            {payload?.lastTestError ? (
              <p className="text-[11px] leading-snug text-red-800">{String(payload.lastTestError)}</p>
            ) : null}
            {payload?.message && String(payload.message) !== String(payload.lastTestError || '') ? (
              <p className="text-[11px] leading-snug text-gray-700">{String(payload.message)}</p>
            ) : null}
            {lines.length ? (
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-[11px] text-gray-700">
                {lines.map((t, i) => (
                  <li key={`${i}-${t.slice(0, 64)}`} className="break-words">
                    {t}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">Full response</p>
          <pre className="max-h-[40vh] overflow-auto rounded border border-gray-100 bg-gray-900/95 p-2 font-mono text-[10px] leading-relaxed text-gray-100">
            {pretty}
          </pre>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function WhereUsedMenu({ secretId }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refsErr, setRefsErr] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setRefsErr('');
      try {
        const out = await getSecretsRegistryReferences(secretId);
        if (!cancelled) setRefs(Array.isArray(out.references) ? out.references : []);
      } catch (e) {
        if (!cancelled) setRefsErr(e?.message || 'Could not load references');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, secretId]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
        data-testid={`secrets-where-used-${secretId}`}
      >
        Where used
      </button>
      <PortalMenu
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        menuRole="region"
        align="bottom-right"
        className="max-w-sm rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        <div className="max-h-52 min-w-[14rem] overflow-y-auto p-2 text-xs text-gray-800">
          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : refsErr ? (
            <p className="text-red-600">{refsErr}</p>
          ) : refs.length === 0 ? (
            <p className="text-gray-500">Not used by any agent yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {refs.map((r, idx) => (
                <li key={`${r.networkId}-${r.agentId}-${r.instanceId}-${idx}`} className="leading-snug">
                  <span className="font-mono text-[11px] text-gray-900">{String(r.networkId || '—')}</span>
                  <span className="text-gray-400"> · </span>
                  <span className="font-mono text-[11px]">{String(r.agentId || '—')}</span>
                  {r.toolName ? (
                    <>
                      <span className="text-gray-400"> · </span>
                      <span className="text-gray-600">{String(r.toolName)}</span>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </PortalMenu>
    </>
  );
}

// NOTE: the tool-selection grid (ToolGlyph / ToolCard / tabbed picker) now
// lives in the shared ``ToolSelectDialog`` so Connections, Workbench, and the
// Room drawer present an IDENTICAL view + tool list. See agentTools/ToolSelectDialog.jsx.


/**
 * @param {{
 *   catalogNetworkId?: string,
 *   catalogPoolId?: string,
 *   tenantId: string,
 *   title?: string,
 *   subtitle?: string,
 *   allowCreate?: boolean,
 *   allowTenantRegistryMutate?: boolean,
 *   workspaceRole?: string,
 *   poolRole?: string,
 *   viewerUserSub?: string,
 *   networkOwnerUserId?: string,
 * }} props
 */
export default function SecretsCatalog({
  catalogNetworkId = '',
  catalogPoolId = '',
  tenantId,
  title = 'Connections',
  subtitle = null,
  allowCreate = true,
  allowTenantRegistryMutate = false,
  workspaceRole = '',
  poolRole = '',
  viewerUserSub = '',
  networkOwnerUserId = '',
  // One-shot deep-link: when the user clicks "Create in Connections" from the
  // agent tool picker's empty instance list, the host switches to this tab and
  // passes the tool here ('workspace' | an MCP id) so we auto-open its creator.
  // ``onAutoOpenConsumed`` is called once we've acted, so it fires only once.
  autoOpenTool = null,
  onAutoOpenConsumed = null,
  // Round-trip: after a connection is CREATED here, notify the host so it can
  // return the user to the tool picker and auto-assign it. Shape:
  // ``({ tool, secretId, secretName, scope }) => void`` where ``tool`` is
  // 'workspace' | the MCP id | the connector toolName. Optional.
  onConnectionCreated = null,
}) {
  if (!secretsRegistryV1Enabled()) {
    return null;
  }

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // Read-only "View" of a workspace row (files + baked template/skill versions).
  const [viewingWorkspaceId, setViewingWorkspaceId] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [authFilter, setAuthFilter] = useState('');
  const [genericRestTool, setGenericRestTool] = useState(null);
  /** Tool catalog row for ``code-connector`` — passed into the registry wizard (labels, future hints). */
  const [codeConnectorTool, setCodeConnectorTool] = useState(null);
  /** Subset of MCP tools with stored credentials (``service`` + ``secretFields``). */
  const [connectionTools, setConnectionTools] = useState([]);
  const [toolChoices, setToolChoices] = useState([]);
  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  /** ``rest`` · ``ToolGenericRestConfigWizard``; ``codeconnector`` · ``ToolCodeConnectorRegistryWizard``. */
  const [wizardKind, setWizardKind] = useState(null);
  /** When set, wizard opens in edit mode with this ``initialEntry`` shape. */
  const [wizardInitial, setWizardInitial] = useState(undefined);
  /** Authenticated MCPs from the platform registry (id, displayName,
   *  credentialFields, credentialUi) — drives the MCP picker + wizard. */
  const [mcpRegistry, setMcpRegistry] = useState([]);
  /** Configured MCP credential rows (scoped) merged into the catalog list. */
  const [mcpRows, setMcpRows] = useState([]);
  /** When set, the MCP credential wizard (McpCredentialForm) is open for this
   *  MCP at ``registryScope`` (create) or an explicit override scope. */
  const [mcpWizard, setMcpWizard] = useState(null); // { mcp, scope }
  const [mcpBusyKey, setMcpBusyKey] = useState(''); // `${mcpId}:${scopeKind}` mid delete
  const [editLoadingSecretId, setEditLoadingSecretId] = useState('');
  const [testingSecretId, setTestingSecretId] = useState('');
  const [probeBanner, setProbeBanner] = useState('');
  const [deletingSecretId, setDeletingSecretId] = useState('');
  /** Type-to-confirm delete: holds {secretId, name, isWorkspace} for the open dialog. */
  const [pendingDelete, setPendingDelete] = useState(null);
  /** Row test: show probe payload (upstream status is authoritative; API HTTP 200 alone is not). */
  const [registryProbeDialog, setRegistryProbeDialog] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 280);
    return () => clearTimeout(t);
  }, [query]);

  const bootstrapTools = useCallback(async () => {
    try {
      const { tools } = await listTools({ forAgent: true });
      const safe = Array.isArray(tools) ? tools : [];
      const withSecrets = safe.filter((t) => toolSupportsRegistryConnections(t));
      withSecrets.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
      setConnectionTools(withSecrets);
      setGenericRestTool(safe.find((t) => t.name === 'generic-rest-api') || null);
      setCodeConnectorTool(safe.find((t) => t.name === 'code-connector') || null);
      setToolChoices([
        { value: '', label: 'All tools' },
        ...safe.filter((t) => t?.name).map((t) => ({ value: t.name, label: t.name })),
      ]);
    } catch {
      setConnectionTools([]);
      setGenericRestTool(null);
      setCodeConnectorTool(null);
      setToolChoices([{ value: '', label: 'All tools' }]);
    }
    // FULL MCP catalog — the selection view is identical across Connections /
    // Workbench / Room (same tool list + counts). We no longer pre-filter to
    // authRequired here; an unauthenticated MCP (no credential to save) is shown
    // but its pick is handled with a friendly "no setup needed" message rather
    // than an empty wizard (see handlePickConnectionTool).
    try {
      const { mcps } = await listMcpRegistry();
      setMcpRegistry(Array.isArray(mcps) ? mcps : []);
    } catch {
      setMcpRegistry([]);
    }
  }, []);

  const refresh = useCallback(
    async (silent) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError('');
      try {
        // Pass the network + pool so the backend returns the correct
        // inheritance chain for this surface: pool → its own POOL rows + parent
        // NETWORK + TENANT; network → NETWORK + TENANT; tenant → TENANT only.
        // Without poolId the backend EXCLUDES POOL-scoped rows entirely, so a
        // room-scoped connection created here would never appear in its own
        // Connections tab. (Bug: room-scoped 'test-ws2' was invisible because
        // these were omitted + a client filter dropped POOL rows.)
        const { secrets: raw } = await listSecretsRegistry({
          toolName: toolFilter || undefined,
          q: debouncedQ || undefined,
          networkId: catalogNetworkId && catalogNetworkId !== 'new' ? catalogNetworkId : undefined,
          poolId: catalogPoolId || undefined,
        });
        let list = Array.isArray(raw) ? raw : [];
        // Skills (toolName='skill') share the secrets-registry table but are NOT
        // connections — they have their own Skills tab (doc/arch/vertical/SKILLS.md). The
        // registry list returns every tool type, so drop skill rows here so they
        // never appear in Connections. (Workspaces DO belong here — they have a
        // synthetic create tile above — so only 'skill' is excluded.)
        list = list.filter((r) => String(r?.toolName || '') !== 'skill');
        // NOTE: no client-side scope filter — the backend already returns exactly
        // the inheritance chain for (networkId, poolId). The old
        // ``registrySecretVisibleForNetwork`` pass DROPPED POOL rows (it had no
        // pool context) and is why room-scoped rows vanished.
        setRows(list);
      } catch (e) {
        setError(e?.message || 'Could not load connections');
        setRows([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
      // MCP credential rows (scoped) — merged into the same table downstream.
      // Best-effort: a failure here must not blank the connector list.
      try {
        const { mcpCredentials } = await listMcpCredentials({
          networkId: catalogNetworkId && catalogNetworkId !== 'new' ? catalogNetworkId : undefined,
          poolId: catalogPoolId || undefined,
        });
        setMcpRows(Array.isArray(mcpCredentials) ? mcpCredentials : []);
      } catch {
        setMcpRows([]);
      }
    },
    [toolFilter, debouncedQ, catalogNetworkId, catalogPoolId],
  );

  useEffect(() => {
    bootstrapTools();
  }, [bootstrapTools]);

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  useEffect(() => {
    const onRegistrySignal = () => {
      refresh(true);
    };
    window.addEventListener('stepflow-secret-registry-changed', onRegistrySignal);
    return () => window.removeEventListener('stepflow-secret-registry-changed', onRegistrySignal);
  }, [refresh]);

  const registryScope = useMemo(() => {
    const nid = String(catalogNetworkId || '').trim();
    const pid = String(catalogPoolId || '').trim();
    if (nid && nid !== 'new' && pid) {
      return { kind: 'POOL', networkId: nid, poolId: pid };
    }
    if (nid && nid !== 'new') {
      return { kind: 'NETWORK', networkId: nid };
    }
    return { kind: 'TENANT' };
  }, [catalogNetworkId, catalogPoolId]);

  const authTypeChoices = useMemo(() => {
    const labels = [];
    let hasUnset = false;
    for (const r of rows) {
      const t = String(r.authType || '').trim();
      if (!t) hasUnset = true;
      else if (!labels.includes(t)) labels.push(t);
    }
    labels.sort((a, b) => a.localeCompare(b));
    const out = [{ value: '', label: 'All auth types' }];
    if (hasUnset) out.push({ value: '__none__', label: '— (catalog default)' });
    for (const v of labels) out.push({ value: v, label: v });
    return out;
  }, [rows]);

  const mcpById = useMemo(() => {
    const m = {};
    for (const r of mcpRegistry) m[r.id] = r;
    return m;
  }, [mcpRegistry]);

  // Normalize MCP credential rows into the connector table's row shape so they
  // render in the SAME table. ``kind:'mcp'`` flags the actions cell to use the
  // MCP wizard/delete/test instead of the connector ones.
  const mcpRowsNormalized = useMemo(
    () => mcpRows.map((r) => ({
      kind: 'mcp',
      mcpId: r.mcpId,
      instanceId: r.instanceId || 'default',
      label: r.label || '',
      // synthesized stable key — include instanceId so multiple labeled
      // instances of the same MCP at one scope each get their own row.
      secretId: `mcp:${r.mcpId}:${r.instanceId || 'default'}:${r.scope?.kind || 'TENANT'}:${r.scopeId || ''}`,
      // NAME column = the label (the instance name), aligning with connectors
      // where NAME is the secret's name; the tool/MCP id lives in the Tool column.
      // Fall back to the MCP display name for legacy "default" rows with no label.
      secretName: r.label || r.displayName || r.mcpId,
      toolName: `mcp:${r.mcpId}`,
      authType: '',
      scope: r.scope || { kind: 'TENANT' },
      updatedAt: r.setAt || '',
      _mcpInherited: mcpRowIsInherited(r, { catalogNetworkId, catalogPoolId }),
    })),
    [mcpRows, catalogNetworkId, catalogPoolId],
  );

  const filteredRows = useMemo(() => {
    let connectorRows = rows;
    if (authFilter === '__none__') connectorRows = rows.filter((r) => !String(r.authType || '').trim());
    else if (authFilter) connectorRows = rows.filter((r) => String(r.authType || '').trim() === authFilter);
    // Auth filter is connector-only; MCP rows always appended (auth N/A).
    // ``toolFilter`` for mcp:* names still narrows them.
    let mcp = mcpRowsNormalized;
    if (toolFilter) mcp = mcp.filter((r) => r.toolName === toolFilter);
    return [...connectorRows, ...mcp];
  }, [rows, authFilter, mcpRowsNormalized, toolFilter]);

  const connectionToolNameSet = useMemo(
    () => new Set(connectionTools.map((t) => String(t?.name || '').trim()).filter(Boolean)),
    [connectionTools],
  );

  const closeWizards = useCallback(() => {
    setWizardKind(null);
    setWizardInitial(undefined);
    setMcpWizard(null);
  }, []);

  // Round-trip: notify the host that a registry connection was CREATED (only on
  // new entries — edits reuse an existing secretId, nothing new to assign). The
  // wizards' ``onRegistryCreated`` also fires for edits, so the caller passes
  // ``isNew`` to gate. ``saved`` carries ``secretId`` (workspace synthesizes it
  // from the workspaceId; connectors/REST have the real registry id).
  const notifyConnectionCreated = useCallback((toolName, saved, isNew) => {
    if (!isNew || !saved?.secretId) return;
    onConnectionCreated?.({
      tool: toolName,
      secretId: String(saved.secretId).trim(),
      secretName: saved.secretName || toolName,
      scope: saved.scope,
    });
  }, [onConnectionCreated]);

  // Combined "Create New" picker entries: connector tools + authenticated MCPs.
  // MCP entries carry ``__mcpId`` so the pick handler can route them.
  const pickerEntries = useMemo(() => {
    const connectors = connectionTools.map((t) => ({
      category: 'connector',
      name: t.name,
      displayName: t.name,
      description: t.description || 'Stepflow connector',
      comingSoon: false,
      mostUsed: MOST_USED_CONNECTORS.has(t.name),
    }));
    // Synthetic 'workspace' tile — no backing Lambda, injected client-side. The
    // wizard uses the dedicated /tools/workspaces REST surface (upload → unpack).
    connectors.push({
      category: 'connector',
      name: 'workspace',
      displayName: WORKSPACE_PSEUDO_TOOL.displayName,
      description: WORKSPACE_PSEUDO_TOOL.description,
      comingSoon: false,
      mostUsed: true,
    });
    const mcps = mcpRegistry.map((m) => ({
      category: 'mcp',
      name: `mcp:${m.id}`,
      displayName: m.displayName || m.id,
      description: m.comingSoon
        ? 'Setup coming soon'
        : (m.description || `${m.displayName || m.id} (MCP tool)`),
      authRequired: Boolean(m.authRequired),
      verified: Boolean(m.verified),
      __mcpId: m.id,
      __comingSoon: Boolean(m.comingSoon),
      comingSoon: Boolean(m.comingSoon),
      mostUsed: MOST_USED_MCPS.has(m.id),
    }));
    return [...mcps, ...connectors];
  }, [connectionTools, mcpRegistry]);

  const openToolPickerForCreate = useCallback(() => {
    setError('');
    if (!allowCreate) return;
    if (!pickerEntries.length) {
      setError('No credential-based tools or MCPs are available for connections.');
      return;
    }
    setWizardInitial(undefined);
    setWizardKind(null);
    setMcpWizard(null);
    setToolPickerOpen(true);
  }, [allowCreate, pickerEntries]);

  const handlePickConnectionTool = useCallback(
    (tool) => {
      setToolPickerOpen(false);
      setError('');
      setWizardInitial(undefined);
      // MCP entry → open the MCP credential wizard at the mount scope.
      if (tool?.__mcpId) {
        const mcp = mcpById[tool.__mcpId];
        if (!mcp) {
          setError(`MCP “${tool.__mcpId}” is not available.`);
          return;
        }
        if (mcp.comingSoon || tool.__comingSoon) {
          setError(`${mcp.displayName || mcp.id} setup is coming soon — not configurable yet.`);
          return;
        }
        // Unauthenticated MCPs have no credential to save — there's nothing to
        // create in the Connections catalog. Tell the user where to enable it
        // instead of opening an empty wizard.
        if (!mcp.authRequired) {
          setError(
            `${mcp.displayName || mcp.id} needs no credential — enable it directly on an agent `
            + `in Tools & Routing or the room drawer; nothing to save here.`,
          );
          return;
        }
        setMcpWizard({ mcp, scope: registryScope });
        return;
      }
      const name = String(tool?.name || '').trim();
      if (name === 'workspace') {
        setWizardKind('workspace');
        return;
      }
      if (name === 'code-connector') {
        if (!codeConnectorTool) {
          setError('code-connector is not available in the catalog.');
          return;
        }
        setWizardKind('codeconnector');
        return;
      }
      if (name === 'generic-rest-api') {
        if (!genericRestTool) {
          setError('generic-rest-api is not available in the catalog.');
          return;
        }
        setWizardKind('rest');
        return;
      }
      setError(
        `Connection UI for “${name || 'this tool'}” is not implemented — use the Run tab or registry API.`,
      );
    },
    [codeConnectorTool, genericRestTool, mcpById, registryScope],
  );

  // Deep-link consumer: act on ``autoOpenTool`` once (and once the MCP registry
  // is loaded so an MCP id resolves). 'workspace' → workspace wizard; any other
  // value is treated as an MCP id. Calls ``onAutoOpenConsumed`` so re-renders
  // don't re-trigger it.
  useEffect(() => {
    if (!autoOpenTool || !allowCreate) return;
    const t = String(autoOpenTool).trim();
    if (!t) return;
    if (t === 'workspace' || t === 'code-connector' || t === 'generic-rest-api') {
      handlePickConnectionTool({ name: t });
      onAutoOpenConsumed?.();
      return;
    }
    // Otherwise it's an MCP id — wait until the registry is hydrated so mcpById
    // can resolve it; consume only after we have it (or it's known-absent).
    if (Object.keys(mcpById || {}).length === 0) return;
    handlePickConnectionTool({ __mcpId: t });
    onAutoOpenConsumed?.();
  }, [autoOpenTool, allowCreate, mcpById, handlePickConnectionTool, onAutoOpenConsumed]);

  // MCP wizard save/test — wired to the scoped catalog endpoints. The wizard's
  // ``scope`` (mount scope, or an explicit override) determines the row.
  const handleMcpWizardSave = useCallback(
    async (mcpId, credentials, opts = {}) => {
      const scope = mcpWizard?.scope || registryScope;
      // ``label`` names the instance; the backend mints a fresh instanceId from a
      // non-empty label (and reuses an existing one when editing). Without it
      // every save would collapse onto the single "default" instance.
      const label = String(opts.label || '').trim();
      const existingInstanceId = String(mcpWizard?.instanceId || '').trim();
      const body = { mcpId, scope, credentials };
      if (label) body.label = label;
      if (existingInstanceId) body.instanceId = existingInstanceId;
      const created = await createMcpCredential(body);
      setMcpWizard(null);
      refresh(true);
      // Round-trip: surface the new instance so the host can return + assign it.
      // Only on CREATE (not edit) — an edit reuses an existing instanceId.
      if (!existingInstanceId) {
        onConnectionCreated?.({
          tool: mcpId,
          secretId: String(created?.instanceId || '').trim(),
          secretName: label || (created?.label) || mcpId,
          scope,
        });
      }
    },
    [mcpWizard, registryScope, refresh, onConnectionCreated],
  );
  const handleMcpWizardTest = useCallback(
    async (mcpId, credentials) => {
      const scope = mcpWizard?.scope || registryScope;
      return testMcpCredentialScoped({ mcpId, scope, credentials });
    },
    [mcpWizard, registryScope],
  );
  const handleMcpRowDelete = useCallback(
    async (row) => {
      const busyKey = `${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`;
      setMcpBusyKey(busyKey);
      setError('');
      try {
        // Pass the instanceId so a labeled instance is removed (not the legacy
        // "default") — multiple instances share an mcpId+scope.
        await deleteMcpCredential({
          mcpId: row.mcpId,
          scope: row.scope,
          instanceId: row.instanceId || 'default',
        });
        refresh(true);
      } catch (e) {
        setError(e?.message || 'Could not remove MCP connection');
      } finally {
        setMcpBusyKey('');
      }
    },
    [refresh],
  );

  const copySecretId = useCallback(async (sid) => {
    const text = String(sid || '');
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Cable className="h-4 w-4 shrink-0 text-blue-600" aria-hidden />
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <span className="text-[11px] text-gray-500">
              {filteredRows.length} connection{filteredRows.length === 1 ? '' : 's'}
            </span>
          </div>
          {subtitle ? (
            <p className="pl-6 text-[10px] leading-snug text-gray-400">{subtitle}</p>
          ) : catalogNetworkId && catalogNetworkId !== 'new' ? (
            String(catalogPoolId || '').trim() ? (
              <p className="pl-6 text-[10px] leading-snug text-gray-400">
                Tenant, network, and pool-scoped rows visible to this pool (
                <span className="font-mono">{String(catalogPoolId).trim()}</span>
                ).
              </p>
            ) : (
              <p className="pl-6 text-[10px] leading-snug text-gray-400">
                Tenant-wide connections and connections scoped to this network.
              </p>
            )
          ) : (
            <p className="pl-6 text-[10px] leading-snug text-gray-400">
              Reusable API connections and credentials for MCP tools across agents.
            </p>
          )}
          {!allowCreate && String(catalogPoolId || '').trim()
          && catalogNetworkId
          && catalogNetworkId !== 'new' ? (
            <p className="pl-6 text-[10px] leading-snug text-amber-800">
              View-only: only the pool creator or a workspace admin can create connections here.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openToolPickerForCreate}
            disabled={!allowCreate}
            title={
              allowCreate
                ? 'Add a connection'
                : 'Only pool creators and workspace admins can create connections in this room'
            }
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold text-white transition-colors ${
              allowCreate
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'cursor-not-allowed bg-gray-400'
            }`}
          >
            <Plus className="h-3 w-3" aria-hidden /> Create New
          </button>
          <button
            type="button"
            onClick={() => refresh(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            aria-label="Refresh connections list"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or tool"
            className="rounded-md border border-gray-200 py-1 pl-7 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            data-testid="secrets-catalog-search"
          />
        </div>
        <select
          value={toolFilter}
          onChange={(e) => setToolFilter(e.target.value)}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
          data-testid="secrets-catalog-tool-filter"
        >
          {toolChoices.map((o) => (
            <option key={o.value || '_all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={authFilter}
          onChange={(e) => setAuthFilter(e.target.value)}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
          data-testid="secrets-catalog-auth-filter"
        >
          {authTypeChoices.map((o) => (
            <option key={o.value || '_all_auth'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {probeBanner ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{probeBanner}</div>
      ) : null}

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-1.5 text-left">Name</th>
              <th className="px-3 py-1.5 text-left">Tool</th>
              <th className="px-3 py-1.5 text-left">Auth</th>
              <th className="px-3 py-1.5 text-left">Status</th>
              <th className="px-3 py-1.5 text-left">Scope</th>
              <th className="px-3 py-1.5 text-left">Updated</th>
              <th className="px-3 py-1.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No connections yet — create one or adjust filters.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                // MCP credential rows have their own (simpler) shape + actions.
                if (row.kind === 'mcp') {
                  const inherited = row._mcpInherited;
                  const busy = mcpBusyKey === `${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`;
                  return (
                    <tr key={row.secretId} className="border-t border-gray-100 hover:bg-gray-50/80">
                      <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-900">
                        <span className="truncate block" title={String(row.secretName || '')}>
                          {String(row.secretName || '—')}
                        </span>
                        <span className="font-mono text-[10px] text-gray-400">MCP</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{String(row.toolName || '—')}</td>
                      <td className="px-3 py-2 text-[11px] text-gray-400">—</td>
                      <td className="px-3 py-2">
                        {row.configured === false ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                            title="This tool was set up but has no credential yet. Edit it to add one — runs are blocked until then.">
                            Needs credential
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            Configured
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-600">
                        {formatScope(row)}
                        {inherited ? (
                          <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                            Inherited
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-500">
                        {String(row.updatedAt || '—')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-wrap items-center justify-end gap-1">
                          {!allowCreate ? (
                            <span className="text-[11px] text-gray-400">read-only</span>
                          ) : inherited ? (
                            // Inherited (wider-scope, e.g. NETWORK) rows are
                            // DISPLAY-ONLY here — they can only be edited at the
                            // level that owns them (the network's Connections).
                            // No room-level override.
                            <span
                              className="text-[11px] text-gray-400"
                              title={`Inherited from ${formatScope(row)} — edit it at that level`}
                            >
                              Inherited
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                disabled={mcpBusyKey === `edit:${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`}
                                onClick={async () => {
                                  const mcp = mcpById[row.mcpId];
                                  if (!mcp) { setError(`MCP “${row.mcpId}” is not available.`); return; }
                                  // Editing → carry the existing instance so the
                                  // save updates THIS instance (not a new one), and
                                  // rehydrate saved NON-SECRET values so the wizard
                                  // opens pre-populated (parity with connectors).
                                  const ek = `edit:${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind}`;
                                  setMcpBusyKey(ek);
                                  setError('');
                                  let revealed = null;
                                  try {
                                    revealed = await revealMcpCredentialScoped({
                                      mcpId: row.mcpId,
                                      scope: row.scope,
                                      instanceId: row.instanceId || 'default',
                                    });
                                  } catch (e) {
                                    // Soft-fail: still open the wizard (blank prefill)
                                    // rather than block editing entirely.
                                    console.warn('[SecretsCatalog] MCP reveal failed:', e);
                                  } finally {
                                    setMcpBusyKey('');
                                  }
                                  setMcpWizard({
                                    mcp,
                                    scope: row.scope,
                                    instanceId: row.instanceId,
                                    label: revealed?.label || row.label,
                                    initialValues: revealed?.values || null,
                                    initialSecretsSet: revealed?.secretsSet || null,
                                  });
                                }}
                                className="rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                title="Edit / replace this connection's credential"
                                data-testid={`secrets-edit-mcp:${row.mcpId}:${row.instanceId || 'default'}:${row.scope?.kind || 'TENANT'}`}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleMcpRowDelete(row)}
                                className="rounded border border-gray-200 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                {busy ? 'Removing…' : 'Remove'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }
                const canMutateRow = canEditDeleteRegistrySecret({
                  row,
                  catalogNetworkId,
                  catalogPoolId,
                  workspaceRole,
                  poolRole,
                  viewerUserSub,
                  networkOwnerUserId,
                  allowTenantRegistryMutate,
                });
                const mutateLockedHint = registryRowMutateHint(row, { allowTenantRegistryMutate });
                const inheritedHere = registrySecretInheritedHere(row, catalogNetworkId, catalogPoolId);
                return (
                <tr key={String(row.secretId)} className="border-t border-gray-100 hover:bg-gray-50/80">
                  <td className="max-w-[12rem] px-3 py-2 font-medium text-gray-900">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate" title={String(row.secretName || '')}>
                        {String(row.secretName || '—')}
                      </span>
                      {inheritedHere ? (
                        <span
                          className="shrink-0 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-gray-500"
                          title="Inherited from a higher scope (tenant / network). Shown here because it applies to this context — manage it at the scope it was created."
                          data-testid={`secrets-inherited-${row.secretId || ''}`}
                        >
                          Inherited
                        </span>
                      ) : null}
                    </span>
                    <span className="font-mono text-[10px] text-gray-400">{String(row.secretId || '').slice(0, 12)}…</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{String(row.toolName || '—')}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-700">{formatAuthType(row)}</td>
                  <td className="px-3 py-2">
                    {row.configured === false ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                        title="This connection was set up but has no credential yet. Edit it to add one — runs are blocked until then.">
                        Needs credential
                      </span>
                    ) : (
                      <RegistryTestStatusBadge row={row} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600">{formatScope(row)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-[11px] text-gray-500">
                    {String(row.updatedAt || row.updated_at || row.createdAt || '—')}
                    {row.lastTestedAt ? (
                      <span className="mt-0.5 block text-[10px] text-gray-400" title={row.lastTestError || ''}>
                        Tested {String(row.lastTestedAt)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {String(row.toolName || '').trim() === 'workspace' ? (
                        <>
                          <button
                            type="button"
                            title="View this workspace's files + which template / skill versions were baked in (read-only)"
                            onClick={() => setViewingWorkspaceId(String(row.secretId || '').trim())}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
                            data-testid={`workspace-view-${row.secretId}`}
                          >
                            <Eye className="h-3 w-3 shrink-0" aria-hidden /> View
                          </button>
                          {/* Edit = add/remove SKILLS (source files are immutable).
                              Gated by the same down-only authorship as connectors,
                              so it never shows on an inherited (wider-scope) row. */}
                          <button
                            type="button"
                            disabled={!canMutateRow}
                            title={canMutateRow ? 'Add or remove skills for this workspace' : mutateLockedHint}
                            onClick={() => {
                              setError('');
                              setWizardInitial({
                                secretId: String(row.secretId || '').trim(),
                                secretName: row.secretName,
                                scope: row.scope,
                                provenance: row.provenance || (row.config && row.config.provenance),
                              });
                              setWizardKind('workspace');
                            }}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`workspace-edit-${row.secretId}`}
                          >
                            <Pencil className="h-3 w-3 shrink-0" aria-hidden /> Edit
                          </button>
                        </>
                      ) : null}
                      {connectionToolNameSet.has(String(row.toolName || '').trim()) ? (
                        <>
                          <button
                            type="button"
                            disabled={
                              !canMutateRow ||
                              editLoadingSecretId === String(row.secretId || '') ||
                              deletingSecretId === String(row.secretId || '') ||
                              Boolean(testingSecretId && testingSecretId === String(row.secretId || ''))
                            }
                            title={
                              canMutateRow
                                ? 'Edit connection, re-test, and save'
                                : mutateLockedHint
                            }
                            onClick={async () => {
                              const sid = String(row.secretId || '').trim();
                              if (!sid) return;
                              setError('');
                              setProbeBanner('');
                              setEditLoadingSecretId(sid);
                              try {
                                const { secret } = await getSecretsRegistrySecret(sid);
                                const tn = String(secret.toolName || row.toolName || '').trim();
                                if (tn === 'code-connector') {
                                  setWizardInitial(mapRegistryCodeConnectorSecretToInitial(secret));
                                  setWizardKind('codeconnector');
                                } else {
                                  setWizardInitial(mapRegistrySecretToWizardInitial(secret));
                                  setWizardKind('rest');
                                }
                              } catch (e) {
                                setError(e?.message || 'Could not load connection for editing');
                              } finally {
                                setEditLoadingSecretId('');
                              }
                            }}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`secrets-edit-${row.secretId}`}
                          >
                            {editLoadingSecretId === String(row.secretId || '') ? (
                              'Loading…'
                            ) : (
                              <>
                                <Pencil className="h-3 w-3 shrink-0" aria-hidden /> Edit
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            disabled={
                              testingSecretId === String(row.secretId || '') ||
                              editLoadingSecretId === String(row.secretId || '') ||
                              deletingSecretId === String(row.secretId || '')
                            }
                            title={
                              String(row.toolName || '') === 'generic-rest-api'
                                ? 'Run a read-only HTTP probe using stored credentials (authorized by the tools API)'
                                : 'Run the catalog validation test for this connection'
                            }
                            onClick={async () => {
                              const sid = String(row.secretId || '').trim();
                              if (!sid) return;
                              const tn = String(row.toolName || '').trim();
                              const label = String(row.secretName || sid || 'Connection');
                              setProbeBanner('');
                              setTestingSecretId(sid);
                              try {
                                const opts =
                                  tn === 'generic-rest-api'
                                    ? {
                                        networkId: REGISTRY_PROBE_NETWORK_ID,
                                        probeRequest: probeRequestFromGenericRestRegistryRow(row),
                                      }
                                    : { networkId: REGISTRY_PROBE_NETWORK_ID };
                                const data = await testSecretsRegistrySecret(sid, opts);
                                setRegistryProbeDialog({ title: `Connection test · ${label}`, payload: data });
                                await refresh(true);
                              } catch (e) {
                                setProbeBanner(e?.message || 'Probe request failed');
                              } finally {
                                setTestingSecretId('');
                              }
                            }}
                            className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            data-testid={`secrets-test-${row.secretId}`}
                          >
                            {testingSecretId === String(row.secretId || '') ? 'Testing…' : 'Test'}
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        disabled={
                          !canMutateRow ||
                          deletingSecretId === String(row.secretId || '') ||
                          Boolean(testingSecretId && testingSecretId === String(row.secretId || '')) ||
                          editLoadingSecretId === String(row.secretId || '')
                        }
                        title={
                          canMutateRow
                            ? 'Remove this connection (blocked while any agent still references it)'
                            : mutateLockedHint
                        }
                        onClick={() => {
                          const sid = String(row.secretId || '').trim();
                          const label = String(row.secretName || sid || 'connection');
                          if (!sid) return;
                          setProbeBanner('');
                          // Type-to-confirm dialog replaces window.confirm — match
                          // the house DeleteNetworkDialog pattern.
                          setPendingDelete({
                            secretId: sid,
                            name: label,
                            isWorkspace: String(row.toolName || '').trim() === 'workspace',
                          });
                        }}
                        className="inline-flex items-center gap-0.5 rounded border border-red-200 px-1.5 py-0.5 text-[10px] font-medium text-red-800 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid={`secrets-delete-${row.secretId}`}
                      >
                        {deletingSecretId === String(row.secretId || '') ? (
                          'Deleting…'
                        ) : (
                          <>
                            <Trash2 className="h-3 w-3 shrink-0" aria-hidden /> Delete
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        title="Copy connection id"
                        onClick={() => copySecretId(row.secretId)}
                        className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 hover:bg-gray-50"
                        data-testid={`secrets-copy-id-${row.secretId}`}
                      >
                        <Copy className="h-3 w-3" aria-hidden />
                        Copy ID
                      </button>
                      <WhereUsedMenu secretId={String(row.secretId || '')} />
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      <ToolSelectDialog
        open={toolPickerOpen}
        entries={pickerEntries}
        onPick={(t) => handlePickConnectionTool(t)}
        onCancel={() => setToolPickerOpen(false)}
        title="Add a connection"
        subtitle="Pick a tool to connect. MCP tools are the modern, recommended path."
        testidPrefix="secrets-catalog"
        dialogTestId="secrets-catalog-tool-picker"
        cardTestId={(t) => `secrets-catalog-pick-tool-${t.name}`}
      />

      {mcpWizard ? (
        <McpCredentialWizardDialog
          mcp={mcpWizard.mcp}
          scope={mcpWizard.scope}
          initialLabel={mcpWizard.label || ''}
          initialValues={mcpWizard.initialValues || null}
          initialSecretsSet={mcpWizard.initialSecretsSet || null}
          onSave={handleMcpWizardSave}
          onTest={handleMcpWizardTest}
          onCancel={() => setMcpWizard(null)}
        />
      ) : null}

      {wizardKind === 'rest' && genericRestTool && tenantId ? (
        <ToolGenericRestConfigWizard
          key={wizardInitial?.secretId || 'registry-new-rest'}
          tool={genericRestTool}
          open
          isNewEntry={!wizardInitial?.secretId}
          persistTarget="registry"
          registryScope={registryScope}
          initialEntry={wizardInitial}
          networkId={REGISTRY_PROBE_NETWORK_ID}
          agentId="registry-ui"
          env={TOOLS_ENV}
          tenantId={tenantId}
          onRegistryCreated={(saved) => {
            const isNew = !wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            notifyConnectionCreated('generic-rest-api', saved, isNew);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      {wizardKind === 'codeconnector' && tenantId ? (
        <ToolCodeConnectorRegistryWizard
          key={wizardInitial?.secretId || 'registry-new-cc'}
          tool={codeConnectorTool || { name: 'code-connector' }}
          open
          isNewEntry={!wizardInitial?.secretId}
          initialEntry={wizardInitial}
          registryScope={registryScope}
          tenantId={tenantId}
          networkId={REGISTRY_PROBE_NETWORK_ID}
          onRegistryCreated={(saved) => {
            const isNew = !wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            notifyConnectionCreated('code-connector', saved, isNew);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      {wizardKind === 'workspace' && tenantId ? (
        <ToolWorkspaceRegistryWizard
          key={wizardInitial?.secretId ? `registry-edit-workspace-${wizardInitial.secretId}` : 'registry-new-workspace'}
          open
          isNewEntry={!wizardInitial?.secretId}
          initialEntry={wizardInitial}
          registryScope={registryScope}
          tenantId={tenantId}
          onRegistryCreated={(saved) => {
            const wasEdit = !!wizardInitial?.secretId;
            closeWizards();
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('stepflow-secret-registry-changed', {
                    detail: { secretId: saved?.secretId },
                  }),
                );
              }
            } catch {
              /* ignore broadcast failures */
            }
            refresh(true);
            // Only announce a NEW connection on create; an edit just refreshes.
            if (!wasEdit) notifyConnectionCreated('workspace', saved, true);
          }}
          onCancel={() => {
            closeWizards();
          }}
        />
      ) : null}
      <RegistryProbeResultDialog
        open={Boolean(registryProbeDialog)}
        title={registryProbeDialog?.title || 'Connection test'}
        payload={registryProbeDialog?.payload}
        onClose={() => setRegistryProbeDialog(null)}
      />
      <DeleteConnectionDialog
        open={Boolean(pendingDelete)}
        connectionName={pendingDelete?.name || ''}
        isWorkspace={Boolean(pendingDelete?.isWorkspace)}
        onClose={() => {
          if (deletingSecretId) return; // don't dismiss mid-delete
          setPendingDelete(null);
        }}
        onConfirm={async () => {
          const sid = String(pendingDelete?.secretId || '').trim();
          if (!sid) return;
          setDeletingSecretId(sid);
          try {
            // Workspaces delete via their own REST surface (also removes the S3
            // objects); connectors via the secrets-registry. Both reject (409)
            // while an agent still pins the id.
            if (pendingDelete?.isWorkspace) {
              await deleteWorkspace(sid);
            } else {
              await deleteSecretsRegistrySecret(sid);
            }
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('stepflow-secret-registry-changed', { detail: {} }),
              );
            }
            await refresh(true);
            setPendingDelete(null);
          } catch (e) {
            const base = e?.message || 'Delete failed';
            const extra =
              e?.status === 409 &&
              typeof e?.data?.count === 'number' &&
              Number.isFinite(e.data.count)
                ? ` (${e.data.count} agent mount(s); remove mounts first)`
                : '';
            // Surface in the row banner too, then re-throw so the dialog shows it.
            setProbeBanner(`${base}${extra}`);
            throw new Error(`${base}${extra}`);
          } finally {
            setDeletingSecretId('');
          }
        }}
      />
      {viewingWorkspaceId ? (() => {
        const wrow = rows.find((r) => String(r.secretId || '') === viewingWorkspaceId) || {};
        return (
          <WorkspaceViewDialog
            workspaceId={viewingWorkspaceId}
            name={wrow.secretName}
            provenance={wrow.provenance || (wrow.config && wrow.config.provenance)}
            onClose={() => setViewingWorkspaceId('')}
          />
        );
      })() : null}
    </div>
  );
}
